import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UsersThree, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  pending: { label: 'In attesa di conferma', cls: 'bg-amber-100 text-amber-800' },
  declined: { label: 'Rifiutata dall\u2019operatore', cls: 'bg-red-100 text-red-800' },
  confirmed: { label: 'Confermata', cls: 'bg-emerald-100 text-emerald-800' },
};

export const AssignStaffBox = ({ request, onAssigned }) => {
  const [staff, setStaff] = useState([]);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const q = request.appointment_at ? `?at=${encodeURIComponent(request.appointment_at)}` : '';
        const r = await api.get(`/api/center/staff/availability${q}`);
        setStaff((r.data.items || []).filter((s) => s.internal_active));
      } catch { /* silent */ }
    })();
  }, [request.appointment_at]);

  const selected = staff.find((s) => s.professional_id === choice);
  const assignedStatus = request.assigned_internal_name
    ? STATUS_BADGE[request.internal_assignment_status || 'confirmed']
    : null;

  const assign = async () => {
    if (!choice) { toast.error('Scegli un professionista del tuo organico'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/api/center/requests/${request.request_id}/assign`, { professional_id: choice });
      if (r.data.availability_warning) {
        toast.warning(`Pre-assegnata a ${r.data.assigned_internal_name}: è fuori dalla sua disponibilità, deve confermare`);
      } else {
        toast.success(`Prestazione assegnata a ${r.data.assigned_internal_name}`);
      }
      onAssigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore assegnazione');
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200" data-testid={`assign-box-${request.request_id}`}>
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold flex-wrap">
        <UsersThree size={16} weight="duotone" className="text-indigo-700" />
        Assegnazione al tuo organico
        {request.assigned_internal_name && (
          <Badge className="bg-indigo-100 text-indigo-800" data-testid={`assigned-${request.request_id}`}>
            {request.assigned_internal_name}
          </Badge>
        )}
        {assignedStatus && (
          <Badge className={`${assignedStatus.cls} border-0`} data-testid={`assign-status-${request.request_id}`}>
            {assignedStatus.label}
          </Badge>
        )}
      </div>

      {request.internal_assignment_status === 'declined' && (
        <p className="text-xs text-red-700 mb-2" data-testid={`declined-${request.request_id}`}>
          {request.internal_declined_by_name || 'L\u2019operatore'} ha rifiutato
          {request.internal_decline_reason ? `: ${request.internal_decline_reason}` : ''}. Assegna a un altro professionista.
        </p>
      )}

      <p className="text-xs text-foreground/70 mb-2">
        Il cliente vedrà il nominativo del professionista alla conferma della data. Se assegni fuori
        dalla sua disponibilità la prestazione resta <strong>in attesa di conferma</strong>.
      </p>

      {staff.length === 0 ? (
        <p className="text-xs text-amber-800">Nessun professionista attivo in organico: aggiungilo dalla sezione Organico.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger className="h-9 max-w-[300px]" data-testid={`assign-select-${request.request_id}`}>
                <SelectValue placeholder={request.assigned_internal_name ? 'Riassegna a…' : 'Scegli professionista'} />
              </SelectTrigger>
              <SelectContent>
                {staff.map((s) => (
                  <SelectItem key={s.professional_id} value={s.professional_id}>
                    {s.name}
                    {s.available === true ? ' · disponibile' : s.available === false ? ' · non disponibile' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="rounded-full h-9" disabled={busy} onClick={assign} data-testid={`assign-btn-${request.request_id}`}>
              {request.assigned_internal_name ? 'Riassegna' : 'Assegna'}
            </Button>
          </div>

          {selected && selected.available === false && (
            <p className="text-xs text-amber-800 flex items-center gap-1 mt-2" data-testid={`availability-warning-${request.request_id}`}>
              <WarningCircle size={13} /> {selected.name} non è disponibile in questo orario: verrà
              pre-assegnata e dovrà confermare.
            </p>
          )}
          {selected && selected.available === true && (
            <p className="text-xs text-emerald-700 flex items-center gap-1 mt-2" data-testid={`availability-ok-${request.request_id}`}>
              <CheckCircle size={13} /> {selected.name} è disponibile in questo orario.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default AssignStaffBox;
